import { PropsWithChildren } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { formatCents } from '@qb/frontend/utils/formatCurrency';
import { PartDisplayParams } from '@/shared/classes/SPSPageComponentParams/PartDisplayParams';
const getTextByDrawerType = (drawerType: string, partDisplayParams: PartDisplayParams) => {
  const drawerMap = {
    [partDisplayParams.drawer1Type]: partDisplayParams.drawer1InsideText,
    [partDisplayParams.drawer2Type]: partDisplayParams.drawer2InsideText,
    [partDisplayParams.drawer3Type]: partDisplayParams.drawer3InsideText,
    [partDisplayParams.drawer4Type]: partDisplayParams.drawer4InsideText,
    [partDisplayParams.drawer5Type]: partDisplayParams.drawer5InsideText
  };
  return drawerMap[drawerType] || null;
};
type SingleAccordionProps = PropsWithChildren<{
  title: string;
}>;
const SingleAccordion = ({
  title,
  children
}: SingleAccordionProps) => {
  const key = title.replace(/\s/g, '-').toLowerCase();
  return <Accordion key={title} disableGutters square sx={{
    my: 2,
    borderColor: 'grey.30p',
    '&.Mui-expanded': {
      borderColor: 'common.backgroundColor',
      backgroundColor: 'common.backgroundColor'
    },
    '&:before': {
      display: 'none'
    }
  }} data-sentry-element="Accordion" data-sentry-component="SingleAccordion" data-sentry-source-file="PartDisplayPageFAQ.tsx">
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`faq-content-${key}`} sx={{
      px: [2, 3, 6],
      '.MuiAccordionSummary-content': {
        my: 3,
        transition: theme => theme.transitions.create(['margin-bottom'])
      },
      '.MuiAccordionSummary-content.Mui-expanded': {
        mb: 1
      }
    }} data-sentry-element="AccordionSummary" data-sentry-source-file="PartDisplayPageFAQ.tsx">
        <Typography variant="h5" data-sentry-element="Typography" data-sentry-source-file="PartDisplayPageFAQ.tsx">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails id={`faq-content-${key}`} sx={{
      px: [2, 3, 6],
      pt: 0,
      pb: 3
    }} data-sentry-element="AccordionDetails" data-sentry-source-file="PartDisplayPageFAQ.tsx">
        <Typography variant="paragraph" align="justify" sx={{
        whiteSpace: 'pre-line'
      }} data-sentry-element="Typography" data-sentry-source-file="PartDisplayPageFAQ.tsx">
          {children}
        </Typography>
      </AccordionDetails>
    </Accordion>;
};
type PartDisplayPageFaqProps = {
  partName: string;
  partDescription: string;
  clientName: string;
  stock: number;
  price: number | null;
  partDisplayParams: PartDisplayParams;
};
export const PartDisplayPageFaq = ({
  partName,
  partDescription,
  clientName,
  stock,
  price,
  partDisplayParams
}: PartDisplayPageFaqProps) => {
  const shippingText = getTextByDrawerType(PartDisplayParams.drawerTypes.shipping, partDisplayParams);
  const warrantyText = getTextByDrawerType(PartDisplayParams.drawerTypes.warranty, partDisplayParams);
  return <Container sx={{
    py: 6
  }} data-sentry-element="Container" data-sentry-component="PartDisplayPageFaq" data-sentry-source-file="PartDisplayPageFAQ.tsx">
      <Typography variant="h4" sx={{
      mb: 8,
      textAlign: 'center'
    }} data-sentry-element="Typography" data-sentry-source-file="PartDisplayPageFAQ.tsx">
        FAQ
      </Typography>
      <Box data-sentry-element="Box" data-sentry-source-file="PartDisplayPageFAQ.tsx">
        {!!price && <SingleAccordion title="Price">
            What is the price of {partName}?
            <br />
            Purchase the {partName} from {clientName} at the price of{' '}
            {formatCents(price)}. Order today.
          </SingleAccordion>}
        <SingleAccordion title="In-stock availability" data-sentry-element="SingleAccordion" data-sentry-source-file="PartDisplayPageFAQ.tsx">
          {stock > 0 ? `${stock} in stock` : 'Out if stock but available for backorder.'}
        </SingleAccordion>
        {!!shippingText && <SingleAccordion title="Shipping">{shippingText}</SingleAccordion>}
        {!!warrantyText && <SingleAccordion title="Warranty">{warrantyText}</SingleAccordion>}
        <SingleAccordion title="Product Details" data-sentry-element="SingleAccordion" data-sentry-source-file="PartDisplayPageFAQ.tsx">
          {partDescription}
        </SingleAccordion>
      </Box>
    </Container>;
};